import { faStar as lightStar } from '@fortawesome/pro-light-svg-icons';
import { faCalendarAlt, faEye } from '@fortawesome/pro-solid-svg-icons';
import { faCartShopping } from '@fortawesome/pro-solid-svg-icons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { faEdit } from '@fortawesome/pro-solid-svg-icons';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Card, ListGroup, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { tss } from 'tss-react';

import StatusDot from 'components/desktop/ShoppingCart/Search/misc/StatusDot';
import FormatedDate from 'components/desktop/_general/FormatedDate/FormatedDate';
import useTranslation from 'hooks/useTranslation';
import { ShoppingCart } from 'types/ShoppingCart/ShoppingCart';
import STATUS from 'types/ShoppingCart/Status';

interface IProps {
	cart: ShoppingCart.Type;
	nextStateLabel: string | undefined;
	previousStateLabel: string | undefined;
	functions: {
		edit: () => void;
		delete: () => void;
		setToDefault: () => void;
		moveToNextState: () => void;
		moveToPrevState: () => void;
	};
}

const ListItem = ({ cart, functions }: IProps) => {
	const { classes } = useStyles({ isDefault: cart.default });
	const { tl, t } = useTranslation();

	const cartStatus = (cart: ShoppingCart.Type): JSX.Element => {
		const informations = {
			[STATUS.LIVE]: {
				status: t('shoppingCart:OPEN'),
				button: {
					text: t('shoppingCart:SET_FOR_REVIEW'),
					function: functions.moveToNextState,
				},
			},
			[STATUS.REVIEW]: {
				status: t('shoppingCart:IN_REVIEW'),
				button: {
					text: t('shoppingCart:TURN_LIVE'),
					function: functions.moveToPrevState,
				},
			},
			[STATUS.APPROVED]: {
				status: t('shoppingCart:CLOSED'),
				button: undefined,
			},
		};
		const information = informations[cart.status];

		return (
			<ListGroup.Item>
				<div className={classes.statusArea}>
					<div>
						{t('shoppingCart:STATUS')}:{' '}
						<span className={classes.bolderSpan}>
							{information.status} <StatusDot status={cart.status} />
						</span>
					</div>
					{information.button && (
						<Button
							size="sm"
							variant="outline-secondary"
							onClick={information.button.function}
							className="ms-2"
						>
							{information.button.text}
						</Button>
					)}
				</div>
			</ListGroup.Item>
		);
	};

	const cartTitle = (cart: ShoppingCart.Type): JSX.Element => (
		<Card.Body>
			<Card.Title>
				<FontAwesomeIcon icon={faCartShopping} className="me-2" />
				{tl(cart.name)}
			</Card.Title>
			<Card.Text>{tl(cart.description)}</Card.Text>
		</Card.Body>
	);

	const cartMetadata = (cart: ShoppingCart.Type): JSX.Element => (
		<ListGroup className="list-group-flush">
			{cartStatus(cart)}
			<ListGroup.Item>
				<FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
				{t('shoppingCart:CREATION_DATE')}:
				<span className={classes.bolderSpan}>
					{' '}
					<FormatedDate date={cart.lastModified} />
				</span>
			</ListGroup.Item>
			<ListGroup.Item>
				<FontAwesomeIcon icon={faUser} className="me-2" />
				{t('shoppingCart:RESPONSIBLE')}:
				<span className={classes.bolderSpan}>
					{' '}
					{ShoppingCart.getFullNameOfAdmin(cart) || (
						<span style={{ color: 'gray' }}>{t('shoppingCart:NO_RESPONSIBLE')}</span>
					)}
				</span>
			</ListGroup.Item>
		</ListGroup>
	);

	const cartActions = (cart: ShoppingCart.Type): JSX.Element => (
		<Card.Body>
			{cart.status === STATUS.APPROVED ? (
				<button className="btn btn-outline-secondary me-2" onClick={functions.edit}>
					<FontAwesomeIcon icon={faEye} />
					<span className="ms-2">{t('shoppingCart:DETAILS')}</span>
				</button>
			) : (
				<button className="btn btn-outline-secondary me-2" onClick={functions.edit}>
					<FontAwesomeIcon icon={faEdit} />
					<span className="ms-2">{t('shoppingCart:EDIT')}</span>
				</button>
			)}
			{cart.status === STATUS.REVIEW && (
				<button
					className="btn btn-outline-secondary me-2"
					onClick={functions.moveToNextState}
				>
					<FontAwesomeIcon icon={faCheck} />
					<span className="ms-2">{t('shoppingCart:CLOSE')}</span>
				</button>
			)}
			<button className="btn btn-outline-danger me-2" onClick={functions.delete}>
				<FontAwesomeIcon icon={faTrash} />
			</button>
			{cart.status === STATUS.LIVE && (
				<>
					<OverlayTrigger
						placement="top"
						overlay={<Tooltip>{t('shoppingCart:SET_MAIN_SHOPPING_CART')}</Tooltip>}
					>
						{cart.default ? (
							<button className="btn btn-outline-primary ">
								<FontAwesomeIcon className="fa-regular" icon={faStar} />
							</button>
						) : (
							<button
								onClick={functions.setToDefault}
								className="btn btn-outline-primary "
							>
								<FontAwesomeIcon icon={lightStar} />
							</button>
						)}
					</OverlayTrigger>
				</>
			)}
		</Card.Body>
	);

	return (
		<Card key={cart.id} className={classes.cartCard}>
			{cartTitle(cart)}
			{cartMetadata(cart)}
			{cartActions(cart)}
		</Card>
	);
};

export default ListItem;

const useStyles = tss.withParams<{ isDefault: boolean }>().create(({ isDefault }) => ({
	cartCard: {
		border: isDefault ? '1px solid #2D5776' : '1px solid #cbcff9',
		borderRadius: '1rem 1rem 1rem 1rem',
		backgroundColor: '#f5f6ff'
	},
	bolderSpan: {
		fontWeight: '500',
	},
	statusArea: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
}));
